import { AgmCoreModule } from '@agm/core';
import { AgmJsMarkerClustererModule } from '@agm/js-marker-clusterer';
import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AppAsideModule,
  AppBreadcrumbModule,
  AppSidebarModule
} from '@coreui/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ChartsModule, ThemeService } from 'ng2-charts';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { CookieService } from 'ngx-cookie-service';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { ToastNotificationsModule } from 'ngx-toast-notifications';
import { BreadcrumbModule, BreadcrumbService } from 'xng-breadcrumb';
// Import 3rd party components
import { AppMaterialModule } from './app-material/app-material.module';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
// Import routing module
import { AppRoutingModule } from './app.routing';
// Import containers
import { DefaultLayoutComponent } from './containers';
import { FooterModule } from './core-components/footer/footer.module';
import { HeaderModule } from './core-components/header/header.module';
import { HttpLoaderFactory } from './core-components/language/language.module';
import { MyLoaderComponent } from './core-components/my-loader/my-loader.component';
import { SidenavModule } from './core-components/sidenav/sidenav.module';
import { CdkDetailRowDirective } from './directives/cdk-detail-row.directive';
import { HideMeDirective } from './directives/hide-me.directive';
import { MyRemoveIfFeatureOffDirective } from './directives/remove-if-feature-off.directive';
import { MyRemoveIfFeatureOnDirective } from './directives/remove-if-feature-on.directive';
import { ShowColumnDirective } from './directives/show-column.directive';
import { FeatureFlagService } from './feature-flag.service';
import { HttpErrorInterceptor } from './helpers/interceptors/httpErrorInterceptor';
import { HttpHeadersInterceptor } from './helpers/interceptors/httpHeadersInterceptor';
import { InputErrorPipeModule } from './pipes/input-error-pipe.module';
import { AuthService } from './services/auth.service';
import { LoaderInterceptor } from './services/loader-interceptor.service';
import { LoaderService } from './services/loader.service';
import { LogPublishersService } from './services/log-publishers.service';
import { LogService } from './services/log.service';
import { WebSocketsService } from './services/web-sockets.service';
import { SharedComponentsModule } from './shared-components.module';
import { AuthGuard } from './shared/auth.guard';
import { SecureInnerPagesGuard } from './shared/secure-inner-pages.guard';
import { AccountModule } from './views/account/account.module';
import { ActionDialogueComponent } from './views/action-dialogue/action-dialogue.component';
import { DeleteSensorDialogComponent } from './views/delete-sensor-dialog/delete-sensor-dialog.component';
import { EditSensorDialogComponent } from './views/edit-sensor-dialog/edit-sensor-dialog.component';
import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { LoginModule } from './views/login/login.module';
import { NewSensorDialogComponent } from './views/new-sensor-dialog/new-sensor-dialog.component';
import { SensorInfoDialogComponent } from './views/sensor-info-dialog/sensor-info-dialog.component';
import { UserManagementModule } from './views/user-management/user-management.module';
import {NgxMaterialTimepickerModule} from 'ngx-material-timepicker';
import { MatIconModule } from '@angular/material/icon';




const APP_CONTAINERS = [
  MyLoaderComponent,
  DefaultLayoutComponent
];









@NgModule({
  declarations: [CdkDetailRowDirective, MyRemoveIfFeatureOffDirective, MyRemoveIfFeatureOnDirective, ShowColumnDirective, HideMeDirective],
  imports: [
    CommonModule,
    AppMaterialModule,
    MatPasswordStrengthModule,
    MatIconModule
  ],
  exports: [
    CdkDetailRowDirective,
    MyRemoveIfFeatureOffDirective,
    MyRemoveIfFeatureOnDirective,
    ShowColumnDirective
  ]
})

export class SharedDirectivesModule { }

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}
@NgModule({
  imports: [
    BrowserModule,
    MatIconModule,
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    AppBreadcrumbModule.forRoot(),
    AppAsideModule,
    AppSidebarModule,
    FlexLayoutModule,
    AppMaterialModule,
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    ChartsModule,
    HttpModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    AgmCoreModule,
    AgmJsMarkerClustererModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderModule,
    FooterModule,
    SidenavModule,
    LoginModule,
    UserManagementModule,
    ToastNotificationsModule.forRoot({ duration: 4000, position: 'top-right', autoClose: true, preventDuplicates: true }),
    BreadcrumbModule,
    NgxDaterangepickerMd.forRoot(),
    InputErrorPipeModule,
    SharedComponentsModule,
    SharedDirectivesModule,
    AccountModule,
    MatPasswordStrengthModule,
    NgxMaterialTimepickerModule
  ],
  declarations: [
    AppComponent,
    //@ts-ignore
    ...APP_CONTAINERS,
    P404Component,
    P500Component,
    SensorInfoDialogComponent,
    NewSensorDialogComponent,
    EditSensorDialogComponent,
    DeleteSensorDialogComponent,
    ActionDialogueComponent
  ],
  entryComponents: [
    SensorInfoDialogComponent,
    NewSensorDialogComponent,
    EditSensorDialogComponent,
    DeleteSensorDialogComponent,
    ActionDialogueComponent,
    MyLoaderComponent
  ],
  providers: [
    WebSocketsService,
    AuthService,
    AuthGuard,
    SecureInnerPagesGuard,
    AppConfig,
    LogService,
    LogPublishersService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfig], multi: true
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeadersInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: FeatureFlagService,
      useClass: FeatureFlagService,
      multi: false
    },
    LoaderService,
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    CookieService,
    BreadcrumbService,
    ThemeService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
